import React from 'react';
import { motion } from 'framer-motion';
import { Target, ShieldCheck, CheckCircle, RefreshCw, FileCheck } from 'lucide-react';
import { SectionLabel } from './ui/UI';
import { TiltedCard } from './animations/TiltedCard';
/**
 * The Solution section.
 *
 * Pillars use TiltedCard for premium 3D hover depth — adds craft without
 * shouting.
 */
export function TaglineExplainer() {
  const pillars = [{
    Icon: Target,
    eyebrow: 'Validate what is real',
    body: 'Separate theoretical risk from real exposure.'
  }, {
    Icon: ShieldCheck,
    eyebrow: 'Prove controls work',
    body: 'Or find out they do not before someone else does.'
  }, {
    Icon: CheckCircle,
    eyebrow: 'Fix the right things',
    body: 'Prioritize the paths that actually reduce risk.'
  }, {
    Icon: RefreshCw,
    eyebrow: 'Verify closure',
    body: 'Closed ticket does not mean closed risk.'
  }, {
    Icon: FileCheck,
    eyebrow: 'Show proof',
    body: 'Give customers, auditors, insurers, boards, and executives receipts.'
  }];
  return <section id="solution" aria-labelledby="solution-heading" className="relative w-full py-28 border-t border-white/5" data-id="element-1305">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1306">
        <SectionLabel number="—" label="The Solution" className="mb-8" data-id="element-1307" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14" data-id="element-1308">
          <div className="lg:col-span-7" data-id="element-1309">
            <motion.h2 id="solution-heading" initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6
          }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[1.1] text-white" data-id="element-1310">
              Stop guessing.
              <br data-id="element-1311" />
              <span className="text-white/40" data-id="element-1312">Validate it.</span>
            </motion.h2>
          </div>

          <div className="lg:col-span-5 lg:pt-4 space-y-5" data-id="element-1313">
            <motion.p initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-white/75 leading-relaxed" data-id="element-1314">
              Periscan runs safe validation missions across your environment and
              turns the results into evidence. It shows what is real, what is
              blocked, what is missed, what should be fixed, and whether the fix
              worked.
            </motion.p>
          </div>
        </div>

        {/* 5 value cards. Wrapped in TiltedCard for premium 3D hover depth. */}
        <div className="flex flex-wrap justify-center gap-4" data-id="element-1315">
          {pillars.map((pillar, i) => {
          return <motion.div key={pillar.eyebrow} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.08
          }} className="w-full md:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.666rem)] max-w-sm flex-grow" data-id="element-1316">
                <TiltedCard maxTilt={5} scale={1.015} className="group h-full p-7 rounded-xl bg-[#070a0e] border border-white/[0.06] transition-colors hover:border-[#1a8fd4]/30" data-id="element-1317">
                  <div className="w-10 h-10 rounded-lg border flex items-center justify-center mb-5 bg-[#1a8fd4]/10 border-[#1a8fd4]/25" data-id="element-1318">
                    <pillar.Icon className="w-4 h-4 text-[#1a8fd4]" aria-hidden="true" data-id="element-1319" />
                  </div>
                  <h3 className="text-lg font-semibold text-white tracking-tight leading-snug mb-2" data-id="element-1320">
                    {pillar.eyebrow}
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed" data-id="element-1321">
                    {pillar.body}
                  </p>
                </TiltedCard>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}